import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  CheckCircle2,
  ClipboardList,
  FileSignature,
  Minus,
  Pill,
  Plus,
  Receipt,
  RefreshCw,
  Save,
  Search,
  Stethoscope,
} from 'lucide-react';
import {
  ProcedureIdSchema,
  SignExpenseSchema,
  type CatalogItem,
  type ExpenseLine,
  type ExpenseSheet,
  type ItemKind,
  type Patient,
  type Procedure,
  type ProcedureId,
  type SaveExpenseDraftInput,
  type SignExpenseInput,
  type UserId,
} from '@datazo/contracts';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { avatarGradient } from '@/components/ui/avatar';
import { SignaturePad } from '@/components/SignaturePad';
import { ApiClientError } from '@/shared/api/client';
import { ageFromBirthdate, formatShortDateEs, initials } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import { usePageTitle } from '@/shared/store/page';
import { usePatientById } from '@/features/patients/api';
import { useProcedureById } from '@/features/procedures/api';
import { useCatalogList } from '@/features/catalog/api';
import { useUsersList } from '@/features/users/api';
import { useToast } from '@/shared/toast/toast-context';
import { useExpenseByProcedure, useSaveExpenseDraft, useSignExpense } from './api';

/* -------------------------------------------------------------------------- */
/* Page entry                                                                 */
/* -------------------------------------------------------------------------- */

function isProcedureId(value: string | undefined): value is ProcedureId {
  if (value === undefined) return false;
  return ProcedureIdSchema.safeParse(value).success;
}

export function ExpenseSignPage(): JSX.Element {
  const { procedimientoId: param } = useParams<{ procedimientoId: string }>();
  const validatedId = isProcedureId(param) ? param : undefined;

  const procedureQuery = useProcedureById(validatedId);

  usePageTitle('Hoja de gasto', 'Documentos clínicos · Firma de la enfermera');

  if (validatedId === undefined) {
    return (
      <ErrorState
        title="Identificador inválido"
        detail={`"${param ?? ''}" no es un id de episodio válido.`}
      />
    );
  }

  if (procedureQuery.isLoading) {
    return <Skeleton />;
  }

  if (procedureQuery.isError || procedureQuery.data === undefined) {
    return (
      <ErrorState
        title={
          procedureQuery.isError
            ? 'No fue posible cargar el episodio'
            : 'Episodio no encontrado'
        }
        detail={
          procedureQuery.isError
            ? (procedureQuery.error?.message ?? 'Error inesperado')
            : `El identificador ${validatedId} no existe.`
        }
        onRetry={procedureQuery.isError ? () => void procedureQuery.refetch() : undefined}
      />
    );
  }

  return <ExpenseSignLoaded procedure={procedureQuery.data} />;
}

/* -------------------------------------------------------------------------- */
/* Form types                                                                 */
/* -------------------------------------------------------------------------- */

interface LineForm {
  codigo: string;
  nombre: string;
  tipo: ItemKind;
  cantidad: string;
  unidad: string;
  observaciones: string;
}

/* -------------------------------------------------------------------------- */
/* Loaded view                                                                */
/* -------------------------------------------------------------------------- */

interface LoadedProps {
  procedure: Procedure;
}

function ExpenseSignLoaded({ procedure }: LoadedProps): JSX.Element {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const patientQuery = usePatientById(procedure.pacienteId);
  const sheetQuery = useExpenseByProcedure(procedure.id);
  // La cuenta compartida de enfermería opera la tablet (queda en auditoría como
  // operador). La enfermera que FIRMA legalmente se selecciona del directorio
  // de personal — cada enfermera tiene su propia identidad para payroll. Es el
  // mismo modelo que anestesia (operador != firmante).
  const usersQuery = useUsersList({ page: 1, pageSize: 100 });
  const enfermeras = useMemo(
    () =>
      (usersQuery.data?.items ?? []).filter(
        (u) =>
          u.rol === 'enfermera' &&
          u.estado === 'activo' &&
          u.puedeIniciarSesion !== true,
      ),
    [usersQuery.data],
  );

  const signMutation = useSignExpense();
  const draftMutation = useSaveExpenseDraft();

  const [enfermeraId, setEnfermeraId] = useState<UserId | null>(null);
  const [lineas, setLineas] = useState<LineForm[]>([]);
  const [observaciones, setObservaciones] = useState<string>('');
  const [firmaEnfermera, setFirmaEnfermera] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  // Cargar un BORRADOR preguardado al formulario una vez (retomar donde se dejó).
  const existingIsDraft =
    sheetQuery.data !== undefined &&
    sheetQuery.data !== null &&
    sheetQuery.data.estado === 'borrador';
  const draftLoadedRef = useRef(false);
  useEffect(() => {
    if (draftLoadedRef.current) return;
    const sheet = sheetQuery.data;
    if (sheet === undefined || sheet === null || sheet.estado !== 'borrador') return;
    draftLoadedRef.current = true;
    setEnfermeraId(sheet.enfermeraId);
    setObservaciones(sheet.observaciones ?? '');
    setLineas(
      sheet.lineas.map((l) => ({
        codigo: l.codigo,
        nombre: l.nombre,
        tipo: l.tipo,
        cantidad: String(l.cantidad),
        unidad: l.unidad ?? '',
        observaciones: l.observaciones ?? '',
      })),
    );
  }, [sheetQuery.data]);

  const enfermeraSeleccionada = useMemo(
    () => enfermeras.find((u) => u.id === enfermeraId) ?? null,
    [enfermeras, enfermeraId],
  );

  const isAlreadySigned = procedure.docs.gasto === 'firmado';
  const isTerminal = procedure.estado === 'cerrado' || procedure.estado === 'anulado';
  // La hoja de gasto se habilita con el consentimiento firmado. NO requiere que
  // la anestesia esté firmada — ambas se diligencian en paralelo.
  const consentPending = procedure.docs.consentimiento !== 'firmado';
  const canEdit = !consentPending && !isAlreadySigned && !isTerminal;

  // La cantidad gastada de cada ítem del catálogo se modela como una línea.
  // `getQty`/`setQty` traducen entre el catálogo (siempre visible) y `lineas`.
  const qtyByKey = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of lineas) {
      const n = Number(l.cantidad);
      m.set(`${l.tipo}-${l.codigo}`, isFinite(n) ? n : 0);
    }
    return m;
  }, [lineas]);

  const getQty = (item: CatalogItem): number =>
    qtyByKey.get(`${item.tipo}-${item.codigo}`) ?? 0;

  const setQty = (item: CatalogItem, qty: number): void => {
    const key = `${item.tipo}-${item.codigo}`;
    setLineas((prev) => {
      const idx = prev.findIndex((l) => `${l.tipo}-${l.codigo}` === key);
      if (qty <= 0) {
        return idx >= 0 ? prev.filter((_, i) => i !== idx) : prev;
      }
      const line: LineForm = {
        codigo: item.codigo,
        nombre: item.nombre,
        tipo: item.tipo,
        cantidad: String(qty),
        unidad: idx >= 0 ? (prev[idx]?.unidad ?? '') : '',
        observaciones: idx >= 0 ? (prev[idx]?.observaciones ?? '') : '',
      };
      if (idx >= 0) return prev.map((l, i) => (i === idx ? line : l));
      return [...prev, line];
    });
  };

  const totalItems = lineas.length;
  const totalUnidades = lineas.reduce((acc, l) => {
    const n = Number(l.cantidad);
    return isFinite(n) ? acc + n : acc;
  }, 0);

  const buildInput = (): SignExpenseInput | { _error: string } => {
    if (enfermeraId === null) {
      return { _error: 'Selecciona la enfermera que firma.' };
    }
    if (firmaEnfermera === null) {
      return { _error: 'Falta la firma de la enfermera.' };
    }
    const linesDto: ExpenseLine[] = lineas.map((l) => ({
      codigo: l.codigo.trim(),
      nombre: l.nombre.trim(),
      tipo: l.tipo,
      cantidad: Number(l.cantidad),
      ...(l.unidad.trim() === '' ? {} : { unidad: l.unidad.trim() }),
      ...(l.observaciones.trim() === '' ? {} : { observaciones: l.observaciones.trim() }),
    }));
    const trimmedObs = observaciones.trim();
    return {
      procedimientoId: procedure.id,
      enfermeraId,
      lineas: linesDto,
      ...(trimmedObs === '' ? {} : { observaciones: trimmedObs }),
      firmaEnfermera,
    };
  };

  // Preguardado: mismo contenido pero SIN exigir firma. Requiere enfermera
  // seleccionada; incluye solo líneas con nombre (evita placeholders vacíos).
  const buildDraftInput = (): SaveExpenseDraftInput | { _error: string } => {
    if (enfermeraId === null) {
      return { _error: 'Selecciona la enfermera antes de preguardar.' };
    }
    const linesDto: ExpenseLine[] = lineas
      .filter((l) => l.nombre.trim() !== '')
      .map((l) => ({
        codigo: l.codigo.trim(),
        nombre: l.nombre.trim(),
        tipo: l.tipo,
        cantidad: Math.max(0, Number(l.cantidad) || 0),
        ...(l.unidad.trim() === '' ? {} : { unidad: l.unidad.trim() }),
        ...(l.observaciones.trim() === '' ? {} : { observaciones: l.observaciones.trim() }),
      }));
    const trimmedObs = observaciones.trim();
    return {
      procedimientoId: procedure.id,
      enfermeraId,
      lineas: linesDto,
      ...(trimmedObs === '' ? {} : { observaciones: trimmedObs }),
    };
  };

  const canDraft =
    canEdit && enfermeraId !== null && !draftMutation.isPending && !signMutation.isPending;

  const saveDraft = async (): Promise<void> => {
    setServerError(null);
    const input = buildDraftInput();
    if ('_error' in input) {
      setServerError(input._error);
      return;
    }
    try {
      await draftMutation.mutateAsync(input);
      showToast({
        kind: 'success',
        title: 'Borrador guardado',
        description: 'Puedes salir y volver a terminarlo antes de firmar.',
      });
    } catch (err) {
      const msg =
        err instanceof ApiClientError ? err.message : 'No se pudo preguardar la hoja.';
      setServerError(msg);
      showToast({ kind: 'danger', title: 'No se pudo preguardar', description: msg });
    }
  };

  const disabledReason = ((): string | null => {
    if (!canEdit) {
      if (isAlreadySigned) return 'Este episodio ya tiene la hoja de gasto firmada.';
      if (isTerminal) return 'El episodio está cerrado o anulado — no se puede firmar.';
      if (consentPending) return 'Primero debe firmarse el consentimiento informado.';
      return 'El episodio no está en estado para firmar la hoja de gasto.';
    }
    if (lineas.length === 0) {
      return 'Agrega al menos una línea (insumo o medicamento).';
    }
    if (lineas.some((l) => l.nombre.trim() === '' || Number(l.cantidad) <= 0)) {
      return 'Todas las líneas requieren nombre y cantidad mayor a cero.';
    }
    if (enfermeraId === null) {
      return 'Selecciona la enfermera que firma.';
    }
    if (firmaEnfermera === null || firmaEnfermera.length < 50) {
      return 'Falta la firma de la enfermera.';
    }
    return null;
  })();

  const submit = async (): Promise<void> => {
    setServerError(null);
    const input = buildInput();
    if ('_error' in input) {
      setServerError(input._error);
      return;
    }
    const parsed = SignExpenseSchema.safeParse(input);
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      const path = first?.path.join('.') ?? '';
      setServerError(
        `Datos inválidos${path !== '' ? ` (${path})` : ''}: ${first?.message ?? 'desconocido'}`,
      );
      return;
    }
    try {
      await signMutation.mutateAsync(parsed.data);
      showToast({
        kind: 'success',
        title: 'Hoja de gasto firmada',
        description: `El episodio ${procedure.id} avanzó a estado gasto. Total ${parsed.data.lineas.length} línea${parsed.data.lineas.length === 1 ? '' : 's'}.`,
      });
      navigate(`/procedimientos/${procedure.id}`);
    } catch (err) {
      setServerError(buildErrorMessage(err));
      showToast({
        kind: 'danger',
        title: 'No fue posible firmar la hoja',
        description: buildErrorMessage(err),
      });
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" asChild>
          <Link to={`/procedimientos/${procedure.id}`}>
            <ArrowLeft className="w-4 h-4" /> Volver al episodio
          </Link>
        </Button>
      </div>

      {/* Pre-condition banners */}
      {isAlreadySigned && (
        <SignedBanner sheet={sheetQuery.data ?? null} isLoading={sheetQuery.isLoading} />
      )}
      {!isAlreadySigned && isTerminal && (
        <div className="px-4 py-3 rounded-md bg-danger-soft text-red-800 text-sm border border-red-200 flex items-start gap-2">
          <Ban className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>Episodio terminado — no se puede firmar la hoja de gasto.</span>
        </div>
      )}
      {!isAlreadySigned && !isTerminal && consentPending && (
        <div className="px-4 py-3 rounded-md bg-warning-soft text-amber-900 text-sm border border-amber-200 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            Primero debe firmarse el consentimiento informado.{' '}
            <Link
              to={`/consentimiento/${procedure.id}`}
              className="text-primary font-semibold underline"
            >
              Ir al consentimiento →
            </Link>
          </span>
        </div>
      )}

      {/* Card 1: episodio + paciente */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Episodio y paciente</CardTitle>
            <CardDescription>Verifica los datos antes de firmar.</CardDescription>
          </div>
          <Badge variant="indigo">{procedure.id}</Badge>
        </CardHeader>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <ProcedureInfo procedure={procedure} />
          <PatientInfo
            isLoading={patientQuery.isLoading}
            isError={patientQuery.isError}
            patient={patientQuery.data}
          />
        </div>
      </Card>

      {canEdit && (
        <>
          {/* Card 2 y 3: catálogos siempre visibles con cantidades */}
          <CatalogPickerCard
            kind="insumo"
            title="Insumos"
            description="Marca cuántas unidades se gastaron de cada insumo."
            icon={<Receipt className="w-4 h-4" />}
            getQty={getQty}
            setQty={setQty}
          />
          <CatalogPickerCard
            kind="medicamento"
            title="Medicamentos"
            description="Marca cuántas unidades o dosis se gastaron de cada medicamento."
            icon={<Pill className="w-4 h-4" />}
            getQty={getQty}
            setQty={setQty}
          />

          {/* Resumen de lo marcado */}
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Resumen de consumo</CardTitle>
                <CardDescription>
                  {totalItems === 0
                    ? 'Aún no has marcado consumo. Ajusta las cantidades en los listados de arriba.'
                    : `${totalItems} ítem${totalItems === 1 ? '' : 's'} · ${totalUnidades} unidad${totalUnidades === 1 ? '' : 'es'} en total`}
                </CardDescription>
              </div>
            </CardHeader>
            {totalItems > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Tipo</TableHead>
                    <TableHead className="w-[130px]">Código</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead className="w-[100px] text-right">Cantidad</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lineas.map((l, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Badge variant={l.tipo === 'insumo' ? 'blue' : 'purple'}>
                          {l.tipo === 'insumo' ? 'Insumo' : 'Medic.'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <code className="text-[11px] bg-surface-alt px-1.5 py-0.5 rounded text-text-muted">
                          {l.codigo}
                        </code>
                      </TableCell>
                      <TableCell className="text-sm">{l.nombre}</TableCell>
                      <TableCell className="text-right text-sm font-semibold">
                        {l.cantidad}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>

          {/* Card 4: Observaciones */}
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Observaciones generales (opcional)</CardTitle>
                <CardDescription>
                  Notas relevantes sobre el uso de insumos o incidencias.
                </CardDescription>
              </div>
            </CardHeader>
            <div className="p-6">
              <textarea
                rows={3}
                maxLength={2000}
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Ej. Se consumió una ampolla adicional de propofol por requerimiento intraoperatorio."
              />
              <div className="text-[11px] text-text-muted mt-1">
                {observaciones.length}/2000 caracteres
              </div>
            </div>
          </Card>

          {/* Card 5: Firma + confirmación */}
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Firma de la enfermera</CardTitle>
                <CardDescription>
                  Al firmar, el episodio transita al estado <strong>gasto</strong> y la
                  acción queda registrada en la bitácora.
                </CardDescription>
              </div>
            </CardHeader>
            <div className="p-6 flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="enfermeraFirmante" className="flex items-center gap-2">
                  <Stethoscope className="w-4 h-4" /> Enfermera que firma
                </Label>
                <select
                  id="enfermeraFirmante"
                  value={enfermeraId ?? ''}
                  onChange={(e) =>
                    setEnfermeraId(e.target.value === '' ? null : (e.target.value as UserId))
                  }
                  className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <option value="">— Selecciona la enfermera —</option>
                  {enfermeras.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nombre}
                    </option>
                  ))}
                </select>
                {usersQuery.isLoading && (
                  <div className="text-[11px] text-text-muted">Cargando directorio…</div>
                )}
                {!usersQuery.isLoading && enfermeras.length === 0 && (
                  <div className="text-[11px] text-red-700">
                    No hay enfermeras registradas en el directorio.
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <FileSignature className="w-4 h-4" /> Firma
                  </Label>
                  {firmaEnfermera !== null && (
                    <Badge variant="green">
                      <CheckCircle2 className="w-3 h-3" /> Capturada
                    </Badge>
                  )}
                </div>
                <SignaturePad onChange={setFirmaEnfermera} placeholder="Firma de la enfermera" />
                {enfermeraSeleccionada !== null && (
                  <div className="text-[11px] text-text-muted flex flex-col gap-0.5">
                    <div className="font-semibold text-text">{enfermeraSeleccionada.nombre}</div>
                    {enfermeraSeleccionada.registroProfesional !== undefined &&
                    enfermeraSeleccionada.registroProfesional !== '' ? (
                      <div className="font-mono text-text-soft">
                        {enfermeraSeleccionada.registroProfesional}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              {serverError !== null && (
                <div className="px-3 py-2 rounded-md bg-danger-soft text-red-800 text-sm border border-red-200 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{serverError}</span>
                </div>
              )}

              {disabledReason !== null && (
                <div className="text-xs text-text-muted bg-surface-alt border border-border rounded-md px-3 py-2">
                  {disabledReason}
                </div>
              )}

              <div className="flex flex-wrap gap-2 justify-between items-center">
                {existingIsDraft ? (
                  <span className="inline-flex items-center gap-1.5 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1">
                    <Save className="w-4 h-4" /> Borrador guardado — puedes seguir editando
                  </span>
                ) : (
                  <span />
                )}
                <div className="flex flex-wrap gap-2 justify-end ml-auto">
                  <Button
                    variant="secondary"
                    onClick={() => navigate(`/procedimientos/${procedure.id}`)}
                    disabled={signMutation.isPending}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => void saveDraft()}
                    disabled={!canDraft}
                    title="Guarda un borrador sin firmar para terminarlo después"
                  >
                    <Save className="w-4 h-4" />
                    {draftMutation.isPending ? 'Guardando…' : 'Preguardar'}
                  </Button>
                  <Button
                    onClick={() => void submit()}
                    disabled={disabledReason !== null || signMutation.isPending}
                    title={disabledReason ?? undefined}
                  >
                    {signMutation.isPending ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" /> Firmando…
                      </>
                    ) : (
                      <>
                        <FileSignature className="w-4 h-4" /> Firmar hoja de gasto
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Catálogo: búsqueda con debounce + autocompletado                           */
/* -------------------------------------------------------------------------- */

interface CatalogPickerCardProps {
  kind: ItemKind;
  title: string;
  description: string;
  icon: JSX.Element;
  getQty(item: CatalogItem): number;
  setQty(item: CatalogItem, qty: number): void;
}

/**
 * Catálogo SIEMPRE visible (insumos o medicamentos) con un stepper de cantidad
 * por ítem. La enfermera marca directamente cuánto se gastó de cada uno; no hay
 * que buscar ni "agregar" líneas. El filtro de texto solo acota la lista.
 */
function CatalogPickerCard({
  kind,
  title,
  description,
  icon,
  getQty,
  setQty,
}: CatalogPickerCardProps): JSX.Element {
  const [q, setQ] = useState<string>('');
  const listQ = useCatalogList(kind, { limit: 200 });
  const items = listQ.data ?? [];
  // Los botones +/- suman/restan SIEMPRE 1 unidad completa. Los medicamentos
  // además admiten decimales (dosis fraccionarias) escribiéndolos a mano en el
  // input — por eso el `step` del input es 0.1 para medicamentos.
  const buttonStep = 1;
  const inputStep = kind === 'medicamento' ? 0.1 : 1;

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (term === '') return items;
    return items.filter(
      (it) =>
        it.nombre.toLowerCase().includes(term) ||
        it.codigo.toLowerCase().includes(term),
    );
  }, [items, q]);

  const selectedCount = items.filter((it) => getQty(it) > 0).length;

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle className="flex items-center gap-2">
            {icon} {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        {selectedCount > 0 && (
          <Badge variant="green">
            {selectedCount} marcado{selectedCount === 1 ? '' : 's'}
          </Badge>
        )}
      </CardHeader>
      <div className="p-6 pt-4 flex flex-col gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filtrar por código o nombre…"
            className="pl-9"
          />
        </div>

        {listQ.isError ? (
          <div className="px-3 py-2 rounded-md bg-danger-soft text-red-800 text-xs border border-red-200">
            No fue posible cargar el catálogo. Reintenta más tarde.
          </div>
        ) : listQ.isLoading ? (
          <div className="px-4 py-6 text-text-muted text-xs text-center">
            Cargando catálogo…
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-6 text-text-muted text-xs text-center">
            {items.length === 0
              ? 'No hay ítems en el catálogo.'
              : `Sin resultados para “${q.trim()}”.`}
          </div>
        ) : (
          <div className="border border-border rounded-md divide-y divide-border max-h-[360px] overflow-y-auto">
            {filtered.map((item) => {
              const qty = getQty(item);
              const active = qty > 0;
              return (
                <div
                  key={`${item.tipo}-${item.codigo}`}
                  className={cn(
                    'px-3 py-2 flex items-center gap-3',
                    active && 'bg-primary/5',
                  )}
                >
                  <code className="text-[11px] bg-surface-alt px-1.5 py-0.5 rounded text-text-muted flex-shrink-0">
                    {item.codigo}
                  </code>
                  <span className="text-sm flex-1 truncate">{item.nombre}</span>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() =>
                        setQty(item, Math.max(0, Number((qty - buttonStep).toFixed(2))))
                      }
                      disabled={qty <= 0}
                      aria-label={`Quitar de ${item.nombre}`}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <Input
                      type="number"
                      min="0"
                      step={inputStep}
                      value={qty === 0 ? '' : String(qty)}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        setQty(item, isFinite(n) && n > 0 ? n : 0);
                      }}
                      placeholder="0"
                      className={cn(
                        'h-8 w-16 text-sm text-center',
                        !active && 'text-text-muted',
                      )}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => setQty(item, Number((qty + buttonStep).toFixed(2)))}
                      aria-label={`Agregar de ${item.nombre}`}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Subcomponents                                                              */
/* -------------------------------------------------------------------------- */

function ProcedureInfo({ procedure }: { procedure: Procedure }): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-text-muted text-[11px] uppercase tracking-wider font-semibold">
        Procedimiento
      </div>
      <div className="text-sm">
        <div className="font-semibold text-text">{procedure.tipo}</div>
        <div className="text-text-muted">
          {formatShortDateEs(procedure.fecha)} · {procedure.hora}
          {procedure.sala !== undefined && <> · {procedure.sala}</>}
        </div>
      </div>
    </div>
  );
}

interface PatientInfoProps {
  isLoading: boolean;
  isError: boolean;
  patient: Patient | undefined;
}

function PatientInfo({ isLoading, isError, patient }: PatientInfoProps): JSX.Element {
  if (isLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        <div className="h-4 w-1/2 rounded bg-surface-alt animate-pulse" />
        <div className="h-10 rounded bg-surface-alt animate-pulse" />
      </div>
    );
  }
  if (isError || patient === undefined) {
    return (
      <div className="text-sm text-text-muted">
        No fue posible cargar los datos del paciente.
      </div>
    );
  }
  const edad = ageFromBirthdate(patient.fechaNacimiento);
  return (
    <div className="flex items-start gap-3">
      <div
        className={cn(
          'w-12 h-12 rounded-md grid place-items-center text-white font-bold flex-shrink-0',
          avatarGradient(patient.id),
        )}
      >
        {initials(patient.nombres, patient.apellidos)}
      </div>
      <div className="min-w-0 text-sm">
        <div className="font-semibold text-text truncate">
          {patient.nombres} {patient.apellidos}
        </div>
        <div className="text-text-muted text-xs">
          {patient.tipoDoc} {patient.documento} · {edad} años ·{' '}
          {patient.sexo === 'F' ? 'F' : patient.sexo === 'M' ? 'M' : 'O'}
        </div>
      </div>
    </div>
  );
}

interface SignedBannerProps {
  sheet: ExpenseSheet | null;
  isLoading: boolean;
}

function SignedBanner({ sheet, isLoading }: SignedBannerProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  return (
    <div className="px-4 py-3 rounded-md bg-success-soft text-green-900 text-sm border border-green-200">
      <div className="flex items-start gap-2">
        <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-success" />
        <div className="flex-1">
          <div className="font-semibold">Hoja de gasto firmada</div>
          {sheet !== null && (
            <div className="text-green-800 mt-0.5">
              Firmada por <strong>{sheet.enfermeraId}</strong> el{' '}
              {sheet.firmadoEn ? new Date(sheet.firmadoEn).toLocaleString('es-CO') : ''} ·{' '}
              {sheet.lineas.length} ítem
              {sheet.lineas.length === 1 ? '' : 's'}
            </div>
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)}>
          {open ? 'Ocultar detalles' : 'Ver detalles'}
        </Button>
      </div>
      {open && (
        <div className="mt-3 pt-3 border-t border-green-200">
          {isLoading ? (
            <div className="text-green-800">Cargando hoja…</div>
          ) : sheet === null ? (
            <div className="text-green-800">Hoja no recuperable.</div>
          ) : (
            <SignedSheetDetail sheet={sheet} />
          )}
        </div>
      )}
    </div>
  );
}

function SignedSheetDetail({ sheet }: { sheet: ExpenseSheet }): JSX.Element {
  return (
    <div className="flex flex-col gap-3 text-text">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[60px]">Tipo</TableHead>
            <TableHead className="w-[110px]">Código</TableHead>
            <TableHead>Nombre</TableHead>
            <TableHead className="w-[80px] text-right">Cant.</TableHead>
            <TableHead className="w-[80px]">Unidad</TableHead>
            <TableHead>Obs.</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sheet.lineas.map((l, i) => (
            <TableRow key={i}>
              <TableCell>
                <Badge variant={l.tipo === 'insumo' ? 'blue' : 'purple'}>
                  {l.tipo === 'insumo' ? 'Insumo' : 'Medic.'}
                </Badge>
              </TableCell>
              <TableCell>
                <code className="text-[11px] bg-surface px-1.5 py-0.5 rounded">{l.codigo}</code>
              </TableCell>
              <TableCell className="text-sm">{l.nombre}</TableCell>
              <TableCell className="text-right text-sm font-semibold">{l.cantidad}</TableCell>
              <TableCell className="text-xs text-text-muted">{l.unidad ?? '—'}</TableCell>
              <TableCell className="text-xs text-text-muted">{l.observaciones ?? ''}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {sheet.observaciones !== undefined && sheet.observaciones.trim() !== '' && (
        <div className="text-sm">
          <div className="text-text-muted text-[10px] uppercase tracking-wider font-semibold mb-1">
            Observaciones
          </div>
          <div className="bg-surface-alt border border-border rounded-md px-3 py-2">
            {sheet.observaciones}
          </div>
        </div>
      )}
      <div>
        <Label>Firma de la enfermera</Label>
        <img
          src={sheet.firmaEnfermera ?? undefined}
          alt="Firma de la enfermera"
          className="max-w-[280px] h-auto border border-border rounded-md bg-white"
        />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Skeleton / ErrorState                                                      */
/* -------------------------------------------------------------------------- */

function Skeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-5">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/procedimientos">
          <ArrowLeft className="w-4 h-4" /> Volver
        </Link>
      </Button>
      <Card>
        <div className="p-6 space-y-3" aria-busy="true">
          <div className="h-5 w-1/3 rounded bg-surface-alt animate-pulse" />
          <div className="h-20 rounded bg-surface-alt animate-pulse" />
        </div>
      </Card>
      <Card>
        <div className="p-6 space-y-3" aria-busy="true">
          <div className="h-5 w-1/3 rounded bg-surface-alt animate-pulse" />
          <div className="h-32 rounded bg-surface-alt animate-pulse" />
        </div>
      </Card>
    </div>
  );
}

interface ErrorStateProps {
  title: string;
  detail: string;
  onRetry?(): void;
}

function ErrorState({ title, detail, onRetry }: ErrorStateProps): JSX.Element {
  return (
    <div className="flex flex-col gap-5">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/procedimientos">
          <ArrowLeft className="w-4 h-4" /> Volver
        </Link>
      </Button>
      <Card>
        <div className="p-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-danger-soft text-danger mb-3">
            <ClipboardList className="w-6 h-6" />
          </div>
          <div className="text-text font-bold text-base mb-1">{title}</div>
          <p className="text-text-muted text-sm mb-4">{detail}</p>
          <div className="flex justify-center gap-2">
            {onRetry !== undefined && (
              <Button variant="secondary" onClick={onRetry}>
                <RefreshCw className="w-4 h-4" /> Reintentar
              </Button>
            )}
            <Button variant="secondary" asChild>
              <Link to="/procedimientos">
                <ArrowLeft className="w-4 h-4" /> Volver al listado
              </Link>
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Error mapper                                                               */
/* -------------------------------------------------------------------------- */

function buildErrorMessage(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.status === 422) {
      return err.message !== ''
        ? err.message
        : 'La enfermera seleccionada no es válida.';
    }
    if (err.status === 404) {
      return 'Episodio no encontrado.';
    }
    if (err.status === 409) {
      return err.message !== ''
        ? err.message
        : 'La hoja de gasto no puede firmarse en el estado actual del episodio.';
    }
    if (err.status === 401) {
      return 'Sesión expirada. Vuelve a iniciar sesión.';
    }
    if (err.status === 422 || err.status === 400) {
      return 'Datos inválidos. Revisa los campos del formulario.';
    }
    if (err.status >= 500) {
      return 'Error del servidor. Inténtalo nuevamente.';
    }
    return err.message;
  }
  return 'No fue posible procesar la solicitud.';
}

export default ExpenseSignPage;
